// 工事取得とデータ取得を分ける
import { EventDropArg } from '@fullcalendar/core';
import { ColSpec, ResourceApi } from '@fullcalendar/resource-common';
import { useSnackbar } from 'notistack';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext
} from 'react';
import {
  getSiteProcessDetail,
  getSiteProcessList,
  updateSiteProcess,
  updateOrderChange
} from '../../../../apicalls/process';
import { getConstructionDetails } from '../../../../apicalls/construction';
import {
  TSiteProcessListData,
  TGanttData,
  TSiteProcessDetailData,
  TGanttVisibleRange,
  TGanttResource,
  TGanttEvent
} from '../../../../types/process';
import { TSelectItem } from '../../../../types/common';
import {
  convertApiDateToFormDate,
  convertFormDateToApiDate,
  getSiteProcessStatusName,
  convertToFullCalendarDateString,
  convertToFullCalendarEndDateString,
  convertToFullCalendarId,
  createGanttEvent,
  createGanttResource,
  getEventBackgroundColor,
  getEventTextColor,
  getOrder,
  getSiteProcessVisibleRange,
  log,
  validateGanttPeriod
} from '../../../../utils';
import { checkResData } from '../../../../utils/common';
import { fetchConstructList } from '../../../../utils/processGantChartUtils';
import {
  SITE_PROCESS_STATUSES,
  SITE_PROCESS_STATUS_COMPLETED,
  SITE_PROCESS_STATUS_UNNECESSARY
} from '../../../../const/Const';
import { TConstruction } from '../../../../types/common';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import {
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset
} from 'react-hook-form';
import {
  GANTT_CHART_HTML_ID,
  GANTT_FULL_CALENDAR_CLASS_NAME_CLOSE_SITE_PROCESS,
  GANTT_FULL_CALENDAR_CLASS_NAME_OPEN_SITE_PROCESS,
  GANTT_MINIMUM_HEIGHT,
  GANTT_RESOURCE_ALL_WIDTH,
  GANTT_RESOURCE_ALL_WIDTH_RESPONSIVE_RATIO,
  GANTT_RESOURCE_MIN_WIDTH_RATIO,
  GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH,
  GANTT_SEARCH_HTML_ID,
  GANTT_WINDOW_WIDTH_SMARTPHONE,
  validationMessage
} from '../../../../const/ProcessConst';
import { Button, Typography } from '@material-ui/core';
import { ceil } from 'lodash';
import { useStyles } from './useStyles';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';
import { BasicSelect } from '../../input/BasicSelect';
import { CustomTooltip } from '../../../tooltip/CustomTooltip';
import { tooltipContents } from '../../../tooltip/Const';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';
import {
  siteProcessGanttChartResource,
  siteProcessDeleteGanttResourceIds
} from '../../../../GlobalState';
import { useRecoilState } from 'recoil';

export type TProps = {
  constructId: number;
  setGanttFromTo: Dispatch<
    SetStateAction<{
      from: string;
      to: string;
    }>
  >;
  isOpenAddSiteProcessModal: boolean;
  setIsOpenAddSiteProcessModal: Dispatch<boolean>;
  parentSiteProcessId?: number;
  setParentSiteProcessId: Dispatch<number>;
  reset: UseFormReset<any>;
  getValues: UseFormGetValues<any>;
  clearErrors: UseFormClearErrors<any>;
  reRenderSiteProcessData: () => void;
  setIsLoading: Dispatch<boolean>;
};

export const useHooks = (props: TProps) => {
  const {
    constructId,
    setGanttFromTo,
    isOpenAddSiteProcessModal,
    setIsOpenAddSiteProcessModal,
    parentSiteProcessId,
    setParentSiteProcessId,
    reset,
    getValues,
    clearErrors,
    reRenderSiteProcessData,
    setIsLoading
  } = props;

  const classes = useStyles();

  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  // 共通で利用する現在日時
  const today = new Date().toISOString().split('T')[0].replaceAll('-', '/');

  // fullcalendar参照用
  const calendarRef = useRef<any>();

  // snackbar設定
  const { enqueueSnackbar } = useSnackbar();

  // 工事情報
  const [currentConstruction, setCurrentConstruction] =
    useState<TConstruction>();

  // 初期表示時のガントチャートで表示するデータ
  // 初期表示後はcalendarRefでデータを制御
  const [initialGanttData, setInitialGanttData] = useState<TGanttData>({
    resources: [],
    events: []
  });

  // 初期表示時の表示期間のデータ
  const [initialVisibleRange, setInitialVisibleRange] =
    useState<TGanttVisibleRange>({
      start: '', // 表示期間が入ったときにガントチャートを表示するのでここは空文字にする
      end: ''
    });

  // ローディング用（各モーダルClose時）
  const [isLoadingForModal, setIsLoadingForModal] = useState(false);

  // 期間の検索用データ
  const [searchStartDate, setSearchStartDate] = useState(
    convertToFullCalendarDateString(today)
  );
  const [searchEndDate, setSearchEndDate] = useState(
    convertToFullCalendarEndDateString(today)
  );

  const [, setAddGanttResourceData] = useRecoilState(
    siteProcessGanttChartResource
  );
  const [, setDeletedResourceIds] = useRecoilState(
    siteProcessDeleteGanttResourceIds
  );

  // 検索期間のバリデーション
  const formValidatePeriod = () => {
    if (!validateGanttPeriod(getValues('start_date'), getValues('end_date'))) {
      return validationMessage.ganttSearchPeriod;
    }

    return true;
  };

  const [constructList, setConstructList] = useState<TSelectItem[]>([]);

  // 工程作成済みの工事リストを取得
  useEffect(() => {
    (async () => {
      const list = await fetchConstructList(enqueueSnackbar, true);
      setConstructList(list);
    })();
  }, []);

  // 工程詳細を工程に変換
  const convertSiteProcessDetailToSiteProcess = (
    siteProcessDetail: TSiteProcessDetailData
  ) => {
    const siteProcess: TSiteProcessListData = {
      id: siteProcessDetail.id,
      parent_site_process_id: siteProcessDetail.parent_site_process
        ? siteProcessDetail.parent_site_process.id
        : null,
      parent_site_process_name: siteProcessDetail.parent_site_process
        ? siteProcessDetail.parent_site_process.site_process_name
        : null,
      site_process_name: siteProcessDetail.site_process_name,
      charge_user_name: siteProcessDetail.charge_user
        ? siteProcessDetail.charge_user.name
        : null,
      start_date: siteProcessDetail.start_date,
      end_date: siteProcessDetail.end_date,
      site_task_count: siteProcessDetail.site_task_summary.site_task_count,
      status: siteProcessDetail.status,
      is_alerted: siteProcessDetail.is_alerted,
      alert_count: siteProcessDetail.site_task_summary.site_task_alert_count,
      planned_man_day: siteProcessDetail.planned_man_day,
      actual_man_day: siteProcessDetail.actual_man_day,
      order_id: siteProcessDetail.order_id
    };

    if (
      siteProcess.status === SITE_PROCESS_STATUS_COMPLETED ||
      siteProcess.status === SITE_PROCESS_STATUS_UNNECESSARY
    ) {
      siteProcess.is_alerted = false;
    }

    return siteProcess;
  };

  // 工程一覧を取得してガントのデータに変換する
  const createGanttResourceAndEvent = async () => {
    let ganttResourceList: Array<TGanttResource> = [];
    let ganttEventList: Array<TGanttEvent> = [];

    const resData = await getSiteProcessList(
      constructId,
      undefined,
      undefined,
      undefined
    );
    const data = await checkResData(resData, enqueueSnackbar);

    if (data) {
      const siteProcessDataList = data.data as Array<TSiteProcessListData>;

      // ガントのデータ作成
      ganttResourceList = siteProcessDataList.map(siteProcess => {
        // 大工程の場合、大工程＋小工程のタスク件数を求める
        let allSiteTaskCount = siteProcess.site_task_count;
        if (!siteProcess.parent_site_process_id) {
          siteProcessDataList
            .filter(target => target.parent_site_process_id === siteProcess.id)
            .forEach(target => {
              allSiteTaskCount += target.site_task_count;
            });
        }
        return createGanttResource(
          siteProcess,
          allSiteTaskCount,
          siteProcess.alert_count,
          false,
          null
        );
      });

      ganttResourceList.sort((a, b) => {
        const aOrder = a.orderId || 0;
        const bOrder = b.orderId || 0;
        return aOrder - bOrder;
      });

      ganttEventList = siteProcessDataList.map(siteProcess => {
        return createGanttEvent(siteProcess);
      });
    }

    return { resources: ganttResourceList, events: ganttEventList };
  };

  // 小工程追加モーダル
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleClickAddSiteProcessButton = useCallback(
    (parentSiteProcessId: number) => {
      setParentSiteProcessId(parentSiteProcessId);
      setIsOpenAddSiteProcessModal(true);
    },
    []
  );
  const handleAfterAddSiteProcess = (
    createdSiteProcessDetailData: TSiteProcessDetailData
  ) => {
    setIsLoadingForModal(true); // ローディング開始
    const calendarApi = calendarRef.current.getApi();
    const siteProcess = convertSiteProcessDetailToSiteProcess(
      createdSiteProcessDetailData
    );

    // 小工程追加時、大工程に紐づいている小工程が表示されている時は大工程にイベントを紐づけない
    let addParent = true;
    if (siteProcess.parent_site_process_id) {
      const parentResource = calendarApi.getResourceById(
        convertToFullCalendarId(siteProcess.parent_site_process_id)
      );
      if (parentResource && parentResource.extendedProps.isChildOpen) {
        addParent = false;
      }
    }

    const ganttResource = createGanttResource(siteProcess);
    const ganttEvent = createGanttEvent(siteProcess, addParent);
    setAddGanttResourceData([ganttResource]);
    calendarApi.addResource(ganttResource);
    calendarApi.addEvent(ganttEvent);

    reRenderSiteProcessData();
    setIsLoadingForModal(false); // ローディング終了
    setIsOpenAddSiteProcessModal(false);
  };

  // 工程編集モーダル
  const [isOpenEditSiteProcessModal, setIsOpenEditSiteProcessModal] =
    useState(false);
  const [editSiteProcessId, setEditSiteProcessId] = useState(0);
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleClickSiteProcessName = useCallback((siteProcessId: number) => {
    setEditSiteProcessId(siteProcessId);
    setIsOpenEditSiteProcessModal(true);
  }, []);
  const handleAfterEditSiteProcess = (
    updatedSiteProcessDetailData: TSiteProcessDetailData
  ) => {
    setIsLoadingForModal(true); // ローディング開始
    // 編集したデータを再取得してガントのデータを書き換える
    const calendarApi = calendarRef.current.getApi();
    const siteProcess = convertSiteProcessDetailToSiteProcess(
      updatedSiteProcessDetailData
    );
    const order = getOrder(siteProcess);
    const resource = calendarApi.getResourceById(editSiteProcessId);
    const oldSiteProcess = resource.extendedProps.siteProcess;

    // 大工程が変わったときはタスク件数の再計算を行う
    if (
      siteProcess.parent_site_process_id !==
      oldSiteProcess.parent_site_process_id
    ) {
      const oldParentSiteProcess = calendarApi.getResourceById(
        convertToFullCalendarId(oldSiteProcess.parent_site_process_id)
      );
      oldParentSiteProcess.setExtendedProp(
        'allSiteTaskCount',
        oldParentSiteProcess.extendedProps.allSiteTaskCount -
          oldSiteProcess.site_task_count
      );
      oldParentSiteProcess.setExtendedProp(
        'allAlertSiteTaskCount',
        oldParentSiteProcess.extendedProps.allAlertSiteTaskCount -
          oldSiteProcess.alert_count
      );

      const newParentSiteProcess = calendarApi.getResourceById(
        siteProcess.parent_site_process_id
      );
      newParentSiteProcess.setExtendedProp(
        'allSiteTaskCount',
        newParentSiteProcess.extendedProps.allSiteTaskCount +
          siteProcess.site_task_count
      );
      newParentSiteProcess.setExtendedProp(
        'allAlertSiteTaskCount',
        newParentSiteProcess.extendedProps.allAlertSiteTaskCount -
          siteProcess.alert_count
      );
    }

    // リソース更新
    resource.setProp('title', siteProcess.site_process_name);
    resource.setExtendedProp('order', order);
    resource.setExtendedProp('site_process', siteProcess);
    if (siteProcess.parent_site_process_id) {
      resource.setProp(
        'parentId',
        convertToFullCalendarId(
          siteProcess.parent_site_process_id as number // typescriptの警告がでてしまうので as numberをつける
        )
      );
    }

    // イベント更新
    const event = calendarApi.getEventById(editSiteProcessId);
    event.setExtendedProp('siteProcess', siteProcess);
    event.setExtendedProp('order', order);
    event.setProp('title', siteProcess.site_process_name);
    event.setStart(convertToFullCalendarDateString(siteProcess.start_date));
    event.setEnd(convertToFullCalendarEndDateString(siteProcess.end_date));
    const resourceIds = Array<string>();
    resourceIds.push(convertToFullCalendarId(siteProcess.id));
    if (siteProcess.parent_site_process_id) {
      const parentResource = calendarApi.getResourceById(
        convertToFullCalendarId(siteProcess.parent_site_process_id)
      );
      if (!parentResource.extendedProps.isChildOpen) {
        resourceIds.push(
          convertToFullCalendarId(
            siteProcess.parent_site_process_id as number // typescriptの警告がでてしまうので as numberをつける
          )
        );
      }
    }
    event.setResources(resourceIds);
    event.setProp('textColor', getEventTextColor(siteProcess));
    event.setProp('backgroundColor', getEventBackgroundColor(siteProcess));
    event.setProp('borderColor', getEventBackgroundColor(siteProcess));

    reRenderSiteProcessData();
    setIsLoadingForModal(false); // ローディング終了
    setIsOpenEditSiteProcessModal(false);
  };

  // 工程編集モーダルでの削除処理
  const handleAfterDeleteSiteProcess = () => {
    setIsLoadingForModal(true); // ローディング開始
    const calendarApi = calendarRef.current.getApi();

    // イベントを削除
    const eventList = calendarApi.getEvents();
    eventList
      .filter(
        (event: any) =>
          event.extendedProps.siteProcess.id === editSiteProcessId ||
          event.extendedProps.siteProcess.parent_site_process_id ===
            editSiteProcessId
      )
      .forEach((event: any) => {
        event.remove();
      });

    // 対象のリソースを取得
    const targetResource = calendarApi.getResourceById(
      convertToFullCalendarId(editSiteProcessId)
    );
    const targetSiteProcess = targetResource.extendedSiteProps?.site_process;

    // 小工程の場合は大工程のタスク件数を再計算。大工程の場合は小工程のresourceを削除する
    if (targetSiteProcess?.parent_site_process_id) {
      const parentResource = calendarApi.getResourceById(
        convertToFullCalendarId(targetSiteProcess.parent_site_process_id)
      );
      parentResource.setExtendedProp(
        'allSiteTaskCount',
        parentResource.extendedProps.allSiteTaskCount -
          targetSiteProcess.task_count
      );
      parentResource.setExtendedProp(
        'allAlertSiteTaskCount',
        parentResource.extendedProps.allAlertSiteTaskCount -
          targetSiteProcess.alert_count
      );
    } else {
      const resourceList = calendarApi.getResources();
      resourceList
        .filter(
          (resource: any) =>
            resource.extendedProps.siteProcess.parent_site_process_id ===
            editSiteProcessId
        )
        .forEach((resource: any) => {
          resource.remove();
        });
    }

    // 対象のリソースを削除
    targetResource.setExtendedProp('delete', true);
    targetResource.remove();
    setDeletedResourceIds(prev => [...prev, targetResource.id]);

    // 必要な箇所の再描画とモーダルクローズ
    reRenderSiteProcessData();
    setIsLoadingForModal(false); // ローディング終了
    setIsOpenEditSiteProcessModal(false);
  };

  // 全削除後の処理
  const handleAfterClearSiteProcess = () => {
    setIsLoadingForModal(true);
    const calendarApi = calendarRef.current.getApi();
    const allResourceIds = calendarApi
      .getResources()
      .map((resource: any) => resource.id);

    calendarApi
      .getEvents()
      .forEach((event: { remove: () => any }) => event.remove());
    calendarApi.getResources().forEach((resource: { remove: () => void }) => {
      resource.remove();
    });
    setDeletedResourceIds(prev => [...prev, ...allResourceIds]);
    reRenderSiteProcessData();
    setIsLoadingForModal(false);
    //setIsOpenClearConfirm(false);
  };

  const [isOpenSiteTaskListModal, setIsOpenSiteTaskListModal] = useState(false);
  const [siteProcessIdForSiteTask, setSiteProcessIdForSiteTask] = useState(0);
  const [includeChildSiteProcess, setIncludeChildSiteProcess] = useState(true);
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleClickTaskCount = useCallback((resource: ResourceApi) => {
    const siteProcessId = resource.extendedProps.siteProcess.id;
    const isChildOpen = resource.extendedProps.isChildOpen;
    const isParent =
      resource.extendedProps.siteProcess.parent_site_process_id === null;
    const hasChildResource = resource.getChildren().length > 0;
    setSiteProcessIdForSiteTask(siteProcessId);
    setIsOpenSiteTaskListModal(true);
    setIncludeChildSiteProcess(isParent && !isChildOpen && hasChildResource);
  }, []);
  // タスク一覧モーダルが閉じられた時、タスク件数に変更がある箇所のみ更新する（全件更新は処理時間がかかるため部分更新）
  const handleAfterChangeSiteTask = () => {
    (async () => {
      setIsLoadingForModal(true); // ローディング開始
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        const { resources, events } = await createGanttResourceAndEvent();
        resources.forEach(resource => {
          const calendarResource = calendarApi.getResourceById(resource.id);
          if (calendarResource) {
            if (
              calendarResource.extendedProps.siteProcess.site_task_count !==
              resource.extendedProps.siteProcess.site_task_count
            ) {
              calendarResource.setExtendedProp(
                'siteProcess',
                resource.extendedProps.siteProcess
              );
            }
            if (
              calendarResource.extendedProps.siteTaskCount !==
              resource.extendedProps.allSiteTaskCount
            ) {
              calendarResource.setExtendedProp(
                'allSiteTaskCount',
                resource.extendedProps.allSiteTaskCount
              );
            }
          }
        });
        events.forEach(event => {
          const calendarEvent = calendarApi.getEventById(event.id);
          if (calendarEvent) {
            if (
              calendarEvent.extendedProps.siteProcess.alert_count !==
              event.extendedProps.siteProcess.alert_count
            ) {
              calendarEvent.setExtendedProp(
                'siteProcess',
                event.extendedProps.siteProcess
              );
            }
          }
        });
      }
      reRenderSiteProcessData();
      setIsLoadingForModal(false); // ローディング終了
      setIsOpenSiteTaskListModal(false);
    })();
  };

  // 全工程を開く
  const [isOpenAllSiteProcess, setIsOpenAllSiteProcess] = useState(false);
  const handleOpenAllSiteProcess = (event: ChangeEvent<HTMLInputElement>) => {
    const className = event.target.checked
      ? GANTT_FULL_CALENDAR_CLASS_NAME_OPEN_SITE_PROCESS
      : GANTT_FULL_CALENDAR_CLASS_NAME_CLOSE_SITE_PROCESS;
    const elements = document.querySelectorAll(className);
    elements.forEach((element: any) => {
      element.click();
    });
    setIsOpenAllSiteProcess(event.target.checked);
  };

  // ガントチャート上でのステータス更新処理
  // 工程一覧にないデータは工程詳細を取得してそれを利用する
  const handleEditSiteProcessStatus = useCallback(
    (siteProcess: TSiteProcessListData, status: number) => {
      getSiteProcessDetail(siteProcess.id).then(resData => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          const siteProcessDetail = data.data;
          if (data) {
            updateSiteProcess(
              siteProcess.id,
              siteProcess.site_process_name,
              constructId,
              siteProcess.start_date,
              status,
              siteProcess.parent_site_process_id,
              siteProcessDetail.charge_user?.id,
              siteProcess.end_date,
              siteProcess.planned_man_day,
              siteProcess.actual_man_day,
              siteProcessDetail.note
            ).then((updatedResponse: any) => {
              checkResData(updatedResponse, enqueueSnackbar).then(
                updatedData => {
                  if (updatedData) {
                    const calendarApi = calendarRef.current.getApi();
                    const siteProcess = convertSiteProcessDetailToSiteProcess(
                      updatedData.data
                    );
                    const siteProcessId = convertToFullCalendarId(
                      siteProcess.id
                    );

                    // リソース更新
                    const resource = calendarApi.getResourceById(siteProcessId);
                    resource.setExtendedProp('siteProcess', siteProcess);
                    // イベント更新
                    const event = calendarApi.getEventById(siteProcessId);
                    // ステータスに合わせた色変更
                    event.setProp('textColor', getEventTextColor(siteProcess));
                    event.setProp(
                      'backgroundColor',
                      getEventBackgroundColor(siteProcess)
                    );
                    event.setProp(
                      'borderColor',
                      getEventBackgroundColor(siteProcess)
                    );
                    event.setExtendedProp('siteProcess', siteProcess);
                  }
                }
              );
            });
          }
        });
      });
    },
    []
  );

  // リソースエリアに表示する項目毎の設定
  const resourceAreaTitle: ColSpec = {
    field: 'title',
    headerContent: '工程名',
    headerClassNames: 'resource-default resource-width-large',
    cellClassNames: 'resource-default resource-width-large',
    cellDidMount: args => {
      // 小工程が表示される時、大工程から小工程のイベントを削除する
      if (args.resource?.extendedProps.siteProcess.parent_site_process_id) {
        const events = args.resource?.getEvents();
        const siteProcess = args.resource?.extendedProps.siteProcess;
        if (events) {
          events.forEach(event => {
            if (siteProcess.parent_site_process_id) {
              event.setResources([convertToFullCalendarId(siteProcess.id)]);
            }
          });
        }
        const parentSiteProcess = calendarRef.current
          ?.getApi()
          .getResourceById(
            convertToFullCalendarId(siteProcess.parent_site_process_id)
          );
        if (parentSiteProcess) {
          parentSiteProcess.setExtendedProp('isChildOpen', true);
        }
      }
    },
    cellWillUnmount: args => {
      // 小工程が非表示になる場合、大工程に小工程のイベントを追加する
      if (args.resource?.extendedProps.siteProcess.parent_site_process_id) {
        const events = args.resource?.getEvents();
        const siteProcess = args.resource?.extendedProps.siteProcess;
        if (events) {
          events.forEach(event => {
            if (event.extendedProps.siteProcess.parent_site_process_id) {
              event.setResources([
                convertToFullCalendarId(event.extendedProps.siteProcess.id),
                convertToFullCalendarId(
                  event.extendedProps.siteProcess.parent_site_process_id
                )
              ]);
            }
          });
        }
        const parentSiteProcess = calendarRef.current
          ?.getApi()
          .getResourceById(
            convertToFullCalendarId(siteProcess.parent_site_process_id)
          );
        // 工程削除でリソース削除する場合はisChildOpenは変更しない
        if (parentSiteProcess && !args.resource.extendedProps.delete) {
          parentSiteProcess.setExtendedProp('isChildOpen', false);
        }
      }
    },
    cellContent: args => {
      return (
        <>
          <button
            className='resource-content-button'
            onClick={() => {
              if (args.resource) {
                handleClickSiteProcessName(
                  args.resource.extendedProps.siteProcess.id
                );
              }
            }}
          >
            <Typography>{args.resource?.title}</Typography>
          </button>
        </>
      );
    }
  };

  const resourceAreaStartDate: ColSpec = {
    field: 'startDate',
    headerContent: '開始日',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      return (
        <Typography>
          {args.resource?.extendedProps.siteProcess.start_date}
        </Typography>
      );
    }
  };
  const resourceAreaEndDate: ColSpec = {
    field: 'endDate',
    headerContent: '終了日',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      return (
        <Typography>
          {args.resource?.extendedProps.siteProcess.end_date || ''}
        </Typography>
      );
    }
  };
  const resourceAreaStatus: ColSpec = {
    field: 'status',
    headerContent: 'ステータス',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      const siteProcess = args.resource?.extendedProps.siteProcess;
      return (
        <BasicSelect
          className={`${classes.statusSelectBox} ${classes.statusSelectBoxInRow}`}
          value={siteProcess.status}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            handleEditSiteProcessStatus(
              siteProcess,
              parseNumber(event.target.value) ?? 0
            );
          }}
          disableUnderline
          disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
        >
          {SITE_PROCESS_STATUSES.map(value => (
            <option value={value} key={value}>
              {getSiteProcessStatusName(value)}
            </option>
          ))}
        </BasicSelect>
      );
    }
  };
  const resourceAreaSiteTaskCount: ColSpec = {
    field: 'siteTaskCount',
    headerContent: 'タスク件数',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      // 大工程が閉じてる場合は大工程＋小工程のタスク件数を表示
      const displaySiteTaskCount = args.resource?.extendedProps.isChildOpen
        ? args.resource?.extendedProps.siteProcess.site_task_count
        : args.resource?.extendedProps.allSiteTaskCount;
      return (
        <>
          <button
            className='resource-content-button'
            onClick={() => {
              if (args.resource) {
                handleClickTaskCount(args.resource);
              }
            }}
            disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
          >
            <Typography>{`${displaySiteTaskCount}件`}</Typography>
          </button>
        </>
      );
    }
  };
  const resourceAreaAddSiteProcess: ColSpec = {
    field: 'addSiteProcess',
    headerContent: '',
    headerClassNames: 'resource-default resource-width-small',
    cellClassNames: 'resource-default resource-width-small',
    cellContent: args => {
      return (
        <>
          {!args.resource?.extendedProps.siteProcess.parent_site_process_id && (
            <>
              {authInfo?.role != ROLE['OUTSIDE_PARTNER'] && (
                <CustomTooltip
                  title={tooltipContents.AddSiteProcess}
                  placement='bottom'
                >
                  <Button
                    onClick={() => {
                      if (args.resource) {
                        handleClickAddSiteProcessButton(
                          args.resource.extendedProps.siteProcess.id
                        );
                      }
                    }}
                    className={classes.addProcessButton}
                  >
                    追加
                  </Button>
                </CustomTooltip>
              )}
            </>
          )}
        </>
      );
    }
  };

  // リソースに表示する内容の設定
  const [initialResourceAreaColumns] = useState([
    resourceAreaTitle,
    resourceAreaAddSiteProcess
  ]);
  const [isDisplayAll, setIsDisplayAll] = useState(false);

  // リソースエリアの幅を求める。詳細表示時はリソースエリアが全て見えるようにリソースエリアを多くできるように計算。
  const calculateResourceAreaWidth = (currentDisplayAll = false) => {
    const innerWidth = window.innerWidth;
    let resourceAreaWidth = `${GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH}px`;
    const element = document.getElementById(GANTT_CHART_HTML_ID);
    let width = 0;
    if (element) {
      const clientRect = element.getBoundingClientRect();
      width = clientRect.width;
    }
    if (currentDisplayAll) {
      if (width > GANTT_RESOURCE_ALL_WIDTH) {
        resourceAreaWidth = `${GANTT_RESOURCE_ALL_WIDTH}px`;
      } else {
        resourceAreaWidth = `${ceil(
          width * GANTT_RESOURCE_ALL_WIDTH_RESPONSIVE_RATIO
        )}px`;
      }
    } else {
      if (
        width * GANTT_RESOURCE_MIN_WIDTH_RATIO >
        GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH
      ) {
        resourceAreaWidth = `${GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH}px`;
      } else {
        resourceAreaWidth = `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`;
      }
    }

    // スマホ向け
    if (innerWidth < GANTT_WINDOW_WIDTH_SMARTPHONE) {
      resourceAreaWidth = `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`;
    }

    return resourceAreaWidth;
  };

  // ガントチャート上での工程編集
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleEditSiteProcess = useCallback(
    (eventInfo: EventDropArg | EventResizeDoneArg) => {
      if (
        eventInfo.event.startStr === eventInfo.oldEvent.startStr &&
        eventInfo.event.endStr === eventInfo.oldEvent.endStr
      ) {
        return;
      }
      let endStr = eventInfo.event.endStr;
      if (endStr && endStr !== '') {
        const endDate = new Date(endStr);
        endDate.setDate(endDate.getDate() - 1);
        endStr = endDate.toISOString().split('T')[0];
      }
      const siteProcess = eventInfo.event.extendedProps.siteProcess;
      const newStartDate = convertFormDateToApiDate(
        eventInfo.event.startStr
      ) as string; // 開始日は必ずあるのでas stringを利用
      let newEndDate = convertFormDateToApiDate(endStr);
      if (newStartDate === newEndDate) {
        newEndDate = null;
      }
      getSiteProcessDetail(siteProcess.id).then(resData => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          const siteProcessDetail = data.data;
          if (siteProcessDetail) {
            updateSiteProcess(
              siteProcess.id,
              siteProcess.site_process_name,
              constructId,
              newStartDate,
              siteProcess.status,
              siteProcess.parent_site_process_id,
              siteProcess.charge_user?.id,
              newEndDate,
              siteProcess.planned_man_day,
              siteProcess.actual_man_day,
              siteProcessDetail.note
            ).then(updatedResponse => {
              checkResData(updatedResponse, enqueueSnackbar).then(
                updatedData => {
                  if (updatedData) {
                    const siteProcess = convertSiteProcessDetailToSiteProcess(
                      updatedData.data
                    );
                    // イベント更新
                    eventInfo.event.setExtendedProp('siteProcess', siteProcess);
                    // イベントのリソースを更新
                    const resourceList = eventInfo.event.getResources();
                    resourceList.forEach(resource => {
                      if (resource.id === eventInfo.event.id) {
                        resource.setExtendedProp('siteProcess', siteProcess);
                      }
                    });
                    reRenderSiteProcessData();
                  } else {
                    eventInfo.revert();
                  }
                }
              );
            });
          }
        });
      });
    },
    [searchStartDate, searchEndDate]
  );

  // 工事ID切り替わり時の工事データ取得
  useEffect(() => {
    handleChangeConstructId();
  }, [constructId]);

  // 工事ID切り替わり時の工程データ取得と初回の表示範囲の設定
  const handleChangeConstructId = async () => {
    if (constructId > 0) {
      setIsOpenAllSiteProcess(false);
      setIsDisplayAll(false);
      setIsLoading(true);

      const [constructionRes, siteProcessRes] = await Promise.all([
        getConstructionDetails(constructId),
        getSiteProcessList(constructId)
      ]);

      const constructionData = await checkResData(
        constructionRes,
        enqueueSnackbar
      );
      const siteProcessData = await checkResData(
        siteProcessRes,
        enqueueSnackbar
      );

      if (constructionData && siteProcessData) {
        const constructionDetail = constructionData.data as TConstruction;
        const siteProcessDataList =
          siteProcessData.data as Array<TSiteProcessListData>;

        setCurrentConstruction(constructionDetail);

        const { resources, events } = await createGanttResourceAndEvent();
        const siteProcessVisibleRange = getSiteProcessVisibleRange(
          constructionDetail,
          resources
        );

        const start =
          constructionDetail.expected_contract_date ||
          siteProcessVisibleRange.start;
        const end = (() => {
          const expected = constructionDetail.expected_end_date;
          const visible = siteProcessVisibleRange.end;

          if (!expected) return visible;
          if (!visible) return expected;

          return expected < visible ? visible : expected;
        })();

        updateDisplayRange(start, end);

        reset({
          start_date: start,
          end_date: convertApiDateToFormDate(end)
        });
        setSearchStartDate(start || '');
        setSearchEndDate(end || '');

        setInitialGanttData({ resources, events });
      }

      setIsLoading(false);
    }
  };

  // 検索の開始日が変更になったときのイベントハンドラ
  const handleChangeStartDatePicker = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const currentStartDate = event.target.value;
    if (searchEndDate) {
      // 表示を更新
      updateDisplayRange(currentStartDate, searchEndDate);
    }
    setSearchStartDate(currentStartDate);

    // 必須以外のバリデーションは開始日と終了日で共通なので、両方に同じエラーが発生しないように一度片方をリセット
    if (getValues('end_date') !== '') {
      clearErrors('end_date');
    }
  };

  // 検索の終了日が変更になったときのイベントハンドラ
  const handleChangeEndDatePicker = (event: ChangeEvent<HTMLInputElement>) => {
    const currentEndDate = event.target.value;
    if (searchStartDate) {
      // 表示を更新
      updateDisplayRange(searchStartDate, currentEndDate);
    }
    setSearchEndDate(currentEndDate);

    // 必須以外のバリデーションは開始日と終了日で共通なので、両方に同じエラーが発生しないように一度片方をリセット
    if (getValues('start_date') !== '') {
      clearErrors('start_date');
    }
  };

  // ガントチャート表示を更新
  const updateDisplayRange = (startDate: string, endDate: string) => {
    if (
      startDate &&
      endDate &&
      startDate <= endDate &&
      validateGanttPeriod(startDate, endDate || '')
    ) {
      const newVisibleRange = {
        start: startDate,
        end: convertToFullCalendarEndDateString(endDate)
      };
      if (calendarRef.current) {
        calendarRef.current.getApi().setOption('visibleRange', newVisibleRange);
      } else {
        setInitialVisibleRange(newVisibleRange);
      }
      // エクセルの出力範囲を設定
      setGanttFromTo({
        from: startDate,
        to: endDate
      });
    }
  };

  // ガントの高さとリソースエリアの幅の設定
  const [initialHeight, setInitialHeight] = useState(GANTT_MINIMUM_HEIGHT);
  const [initialResourceAreaWidth, setInitialResourceAreaWidth] = useState(
    `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`
  );

  // 詳細表示チェックボックスクリック時のハンドラ
  const handleDisplayAll = (event: ChangeEvent<HTMLInputElement>) => {
    const currentDisplayAll = event.target.checked;
    setIsDisplayAll(currentDisplayAll);

    return displayAllChangeView(currentDisplayAll);
  };

  // ウインドウサイズに合わせたガントのサイズ調整
  const displayAllChangeView = (currentDisplayAll = false) => {
    const calculateHeight = () => {
      // 画面内に検索エリア＋ガントエリア＋フッターが収まるようにガントの高さを計算する
      const windowHeight = document.documentElement.clientHeight;
      const headerElement = document.getElementsByTagName('header');
      const headerHeight =
        headerElement && headerElement[0]
          ? headerElement[0].getBoundingClientRect().height
          : 0;
      const footerElement = document.getElementsByClassName('footer');
      const footerHeight =
        footerElement && footerElement[0]
          ? footerElement[0].getBoundingClientRect().height
          : 0;
      const searchContainerElement =
        document.getElementById(GANTT_SEARCH_HTML_ID);
      const searchContainerHeight = searchContainerElement
        ? searchContainerElement.getBoundingClientRect().height
        : 0;
      let height =
        windowHeight - footerHeight - headerHeight - searchContainerHeight - 60; // 60はマージンなどの余白分
      if (height < GANTT_MINIMUM_HEIGHT) {
        height = GANTT_MINIMUM_HEIGHT;
      }

      return height;
    };

    const handleResizeWindow = () => {
      const height = calculateHeight();
      const resourceAreaWidth = calculateResourceAreaWidth(currentDisplayAll);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.setOption('height', height);
        calendarApi.setOption('resourceAreaWidth', resourceAreaWidth);
      }
    };
    window.addEventListener('resize', handleResizeWindow);

    // 詳細表示切り替え時の処理
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (currentDisplayAll) {
        calendarApi.setOption('resourceAreaColumns', [
          resourceAreaTitle,
          resourceAreaStartDate,
          resourceAreaEndDate,
          resourceAreaStatus,
          resourceAreaSiteTaskCount,
          resourceAreaAddSiteProcess
        ]);
      } else {
        calendarApi.setOption('resourceAreaColumns', [
          resourceAreaTitle,
          resourceAreaAddSiteProcess
        ]);
      }
      calendarApi.setOption(
        'resourceAreaWidth',
        calculateResourceAreaWidth(currentDisplayAll)
      );
    } else {
      // 初期設定
      setInitialHeight(calculateHeight());
      setInitialResourceAreaWidth(
        calculateResourceAreaWidth(currentDisplayAll)
      );
    }

    // handleResizeWindowが複数イベントに登録されないように一度削除
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  };

  // ガントチャート上での工程名並び替え
  const handleResourceOrderChange = useCallback(
    (newResources: TGanttResource[]) => {
      const updatePromises = newResources.map((resource, index) => {
        if (resource.id) {
          return updateOrderChange(resource.id, index).then(updatedResponse => {
            return;
          });
        }
        return Promise.resolve();
      });

      Promise.all(updatePromises)
        .then(() => {
          reRenderSiteProcessData();
        })
        .catch(error => {
          console.error('Error updating resources:', error);
        });
    },
    [reRenderSiteProcessData]
  );

  useEffect(() => {
    // コンポーネント描画時にログ出力
    log.info('to process view mode: ganttchart');
    setIsLoading(true);
    if (searchStartDate && searchEndDate) {
      updateDisplayRange(searchStartDate, searchEndDate);
    }
    setTimeout(() => {
      displayAllChangeView();
    }, 0);
  }, []);

  return {
    calendarRef,
    initialHeight,
    initialGanttData,
    searchStartDate,
    searchEndDate,
    handleChangeStartDatePicker,
    handleChangeEndDatePicker,
    isOpenAddSiteProcessModal,
    setIsOpenAddSiteProcessModal,
    parentSiteProcessId,
    handleAfterAddSiteProcess,
    isOpenEditSiteProcessModal,
    setIsOpenEditSiteProcessModal,
    editSiteProcessId,
    handleAfterEditSiteProcess,
    isOpenSiteTaskListModal,
    setIsOpenSiteTaskListModal,
    siteProcessIdForSiteTask,
    handleAfterChangeSiteTask,
    handleEditSiteProcess,
    handleResourceOrderChange,
    handleAfterDeleteSiteProcess,
    handleAfterClearSiteProcess,
    initialVisibleRange,
    isOpenAllSiteProcess,
    handleOpenAllSiteProcess,
    initialResourceAreaColumns,
    isDisplayAll,
    handleDisplayAll,
    initialResourceAreaWidth,
    formValidatePeriod,
    isLoadingForModal,
    includeChildSiteProcess,
    setIncludeChildSiteProcess,
    handleChangeConstructId,
    updateDisplayRange,
    constructList
  };
};
